import { Bot, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import type { UserSettings } from "@/lib/settings";
import { ConfirmDialog } from "./ConfirmDialog";
import { SettingsSection } from "./SettingsSection";

function Stat({ label, value, testId }: { label: string; value: string | number; testId: string }) {
  return (
    <div className="rounded-xl bg-[#F5F6F8] p-4">
      <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1 font-display text-2xl font-extrabold text-[#1B2A4A] tabular" data-testid={testId}>{value}</p>
    </div>
  );
}

export function MentorCard({ mentor }: { mentor: UserSettings["mentor"] }) {
  const pct = mentor.monthly_limit ? Math.min(100, (mentor.used / mentor.monthly_limit) * 100) : 0;
  const clearChat = async () => {
    try {
      const r = await api.delete<{ deleted: number }>("/settings/mentor-conversation");
      toast.success(r.data.deleted ? "Borramos tu conversación con el mentor" : "No había mensajes por borrar");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <SettingsSection id="mentor" icon={Bot} title="Mi mentor" desc="Tus consultas de este mes.">
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Usadas" value={mentor.used} testId="settings-mentor-used" />
        <Stat label="Restantes" value={mentor.remaining} testId="settings-mentor-remaining" />
        <Stat label="Se renuevan" value={mentor.renews_on} testId="settings-mentor-renews" />
      </div>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-[#EEF0F4]">
        <div className="h-full rounded-full bg-[#C9A84C] transition-[width] duration-500" style={{ width: `${pct}%` }} data-testid="settings-mentor-progress" />
      </div>
      <p className="mt-2 text-xs text-slate-500">{mentor.used} de {mentor.monthly_limit} consultas del mes.</p>
      <div className="mt-7 flex flex-col gap-3 border-t border-[#DDE1E8] pt-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-800">Borrar mi conversación con el mentor</p>
          <p className="text-xs text-slate-500">Tus consultas usadas del mes no cambian.</p>
        </div>
        <ConfirmDialog testId="clear-mentor" title="¿Borrar tu conversación?" confirmLabel="Borrar conversación"
          description="Se eliminan todos tus mensajes con el mentor. No se puede deshacer." onConfirm={clearChat}
          trigger={<Button variant="outline" className="h-11 border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="settings-clear-mentor-button"><Trash2 className="mr-2 h-4 w-4" /> Borrar conversación</Button>} />
      </div>
    </SettingsSection>
  );
}
